/**
 * Live pace math for the countdown — PLAN §3, "Live pace math", and
 * deliberately **independent of the solver**.
 *
 * The solver answers "what should I watch?" by searching. This answers a much
 * smaller question the page has to answer sixty times a minute: *at the pace
 * you say you watch, does what's left fit in the time that's left?* It is a
 * handful of divisions over ~81 entries, pure and synchronous, so the ticking
 * bar never waits on anything.
 *
 * The four numbers, exactly as specified:
 *
 * ```
 * remainingMinutes    = Σ unwatched entries in scope
 * requiredHoursPerDay = remainingMinutes / 60 / daysLeft
 * utilization         = requiredHoursPerDay / (hoursPerWeek / 7)
 * status              = utilization <= 0.85 ? "ok" : <= 1 ? "tight" : "infeasible"
 * ```
 *
 * Two modelling decisions worth stating:
 *
 * - **`daysLeft` is whole calendar days, not a fraction.** The clock in the bar
 *   ticks every second, but the pace number must not: "2.4 h/day" creeping to
 *   "2.5 h/day" while you read it is noise, not information. So the seconds
 *   drive the clock and the calendar drives the math.
 * - **Scope is whatever the road is currently showing.** The countdown measures
 *   the plan you're actually looking at, so tightening the tier ceiling or
 *   switching to compressed routes moves the pace immediately. That's the
 *   feedback loop the control panel is for.
 *
 * Nothing here formats copy — every field is a number, a date or an id, and
 * the components decide how to say it.
 */

import type { Entry, EntryId, ISODate, Milestone, TargetEvent } from "@schema/catalog.ts";

const MS_PER_DAY = 86_400_000;
const MINUTES_PER_HOUR = 60;
const DAYS_PER_WEEK = 7;

/** Where the pace sits against your stated budget. */
export type PaceStatus = "ok" | "tight" | "infeasible";

/** Utilization at or below this is comfortable. */
export const PACE_OK_MAX = 0.85;
/** Above this you need more hours than you have. */
export const PACE_TIGHT_MAX = 1;

export function paceStatus(utilization: number): PaceStatus {
  if (utilization <= PACE_OK_MAX) return "ok";
  if (utilization <= PACE_TIGHT_MAX) return "tight";
  return "infeasible";
}

/**
 * `"road"` when Encore mode is off — one countdown to Doomsday. With it on the
 * road splits at the Endgame Encore milestone into `"leg-1"` and `"leg-2"`.
 */
export type LegId = "road" | "leg-1" | "leg-2";

/**
 * Local midnight on the deadline date. Local, not UTC: a deadline that reads
 * "Sept 25" must not tick over on the evening of the 24th because the user is
 * west of Greenwich.
 */
export function deadlineInstant(date: ISODate): number {
  return new Date(`${date}T00:00:00`).getTime();
}

/**
 * Whole calendar days from `now` to the deadline, today included. Never
 * negative — the day of the deadline itself counts as 0 days left.
 *
 * Measured midnight-to-midnight and rounded, not by dividing the raw
 * remainder: a span that crosses a daylight-saving change is 24×n ± 1 hours
 * long, and `ceil` over that turns 137 days into 138 for the hour after
 * midnight. Rounding a midnight-anchored span absorbs the stray hour.
 */
export function daysUntil(deadlineMs: number, now: number): number {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((deadlineMs - today.getTime()) / MS_PER_DAY));
}

/** What one entry costs you, honouring the compressed-route toggle. */
function entryCost(entry: Entry, useAlternatives: boolean): number {
  return useAlternatives ? entry.solver.minMinutes : entry.runtime.minutes;
}

/**
 * A way out of an infeasible leg, in the order PLAN §3 puts them: take the
 * shortcuts, then raise the pace, then give up the nearer deadline, then cut
 * titles. Each carries the number that makes it concrete, and whether it
 * closes the gap **on its own** — an honest callout has to distinguish "this
 * fixes it" from "this helps".
 */
export type Remedy =
  | { kind: "compress"; savedMinutes: number; sufficient: boolean }
  | { kind: "raise-pace"; hoursPerWeekNeeded: number; sufficient: true }
  | { kind: "leave-encore"; extraDays: number; sufficient: boolean }
  | { kind: "trim"; minutes: number; sufficient: true };

/** One countdown: a scope of entries, a deadline, and the pace they imply. */
export interface LegPace {
  id: LegId;
  /** The event this leg is due for — Doomsday, or the Encore. */
  eventTitle: string;
  deadline: ISODate;
  deadlineMs: number;
  /** Milliseconds to the deadline — what the ticking clock renders. Floors at 0. */
  msLeft: number;
  /** Whole calendar days to the deadline — what the pace math divides by. */
  daysLeft: number;

  entryCount: number;
  watchedCount: number;
  remainingCount: number;
  /** Σ cost over every entry in scope, watched or not. */
  totalMinutes: number;
  watchedMinutes: number;
  /** Σ cost over the *unwatched* entries in scope. */
  remainingMinutes: number;
  /** Watched share of `totalMinutes`, 0–1. `1` for an empty scope. */
  progress: number;

  /** `hoursPerWeek / 7` — what you said you have. */
  availableHoursPerDay: number;
  /** `remainingMinutes / 60 / daysLeft` — what this leg asks for. `Infinity` past the deadline. */
  requiredHoursPerDay: number;
  utilization: number;
  status: PaceStatus;
  /** `daysLeft × availableHoursPerDay × 60`. */
  capacityMinutes: number;
  /** How many minutes over capacity this leg is; `0` when it fits. */
  deficitMinutes: number;
  /** Minutes the unwatched entries would give back on their compressed routes; `0` if already compressed. */
  compressibleMinutes: number;
  /** Nothing unwatched left in scope. */
  complete: boolean;
  /** Populated only for infeasible legs; empty otherwise. */
  remedies: Remedy[];
}

interface LegInput {
  id: LegId;
  eventTitle: string;
  deadline: ISODate;
  entries: readonly Entry[];
  watched: Readonly<Record<EntryId, ISODate>>;
  now: number;
  hoursPerWeek: number;
  useAlternatives: boolean;
}

function computeLeg(input: LegInput): LegPace {
  const deadlineMs = deadlineInstant(input.deadline);
  const daysLeft = daysUntil(deadlineMs, input.now);

  let totalMinutes = 0;
  let watchedMinutes = 0;
  let remainingMinutes = 0;
  let watchedCount = 0;
  let compressibleMinutes = 0;

  for (const entry of input.entries) {
    const cost = entryCost(entry, input.useAlternatives);
    totalMinutes += cost;
    if (input.watched[entry.id]) {
      watchedMinutes += cost;
      watchedCount += 1;
    } else {
      remainingMinutes += cost;
      if (!input.useAlternatives) compressibleMinutes += cost - entry.solver.minMinutes;
    }
  }

  const entryCount = input.entries.length;
  const availableHoursPerDay = input.hoursPerWeek / DAYS_PER_WEEK;
  const capacityMinutes = daysLeft * availableHoursPerDay * MINUTES_PER_HOUR;

  // Three cases the bare formula gets wrong: a finished leg needs 0 h/day (not
  // 0/0), a leg past its deadline needs infinitely many, and a budget of zero
  // hours can never absorb any of them.
  const requiredHoursPerDay =
    remainingMinutes === 0
      ? 0
      : daysLeft === 0
        ? Number.POSITIVE_INFINITY
        : remainingMinutes / MINUTES_PER_HOUR / daysLeft;
  const utilization =
    requiredHoursPerDay === 0
      ? 0
      : availableHoursPerDay <= 0
        ? Number.POSITIVE_INFINITY
        : requiredHoursPerDay / availableHoursPerDay;

  return {
    id: input.id,
    eventTitle: input.eventTitle,
    deadline: input.deadline,
    deadlineMs,
    msLeft: Math.max(0, deadlineMs - input.now),
    daysLeft,
    entryCount,
    watchedCount,
    remainingCount: entryCount - watchedCount,
    totalMinutes,
    watchedMinutes,
    remainingMinutes,
    progress: totalMinutes === 0 ? 1 : watchedMinutes / totalMinutes,
    availableHoursPerDay,
    requiredHoursPerDay,
    utilization,
    status: paceStatus(utilization),
    capacityMinutes,
    deficitMinutes: Math.max(0, remainingMinutes - capacityMinutes),
    compressibleMinutes,
    complete: remainingMinutes === 0,
    remedies: [],
  };
}

/** Ask for a believable number of hours — half-hour granularity, always rounded up. */
function roundUpToHalf(hours: number): number {
  return Math.ceil(hours * 2) / 2;
}

/**
 * What would fix this leg. Ordered cheapest-to-you first: shortcuts you
 * already curated, then your own time, then the nearer deadline, then titles.
 *
 * `road` is the same scope measured as a single countdown to the target — the
 * only way to know whether leaving Encore mode would actually rescue leg 1
 * rather than just moving the problem.
 */
function remediesFor(leg: LegPace, road: LegPace, encore: boolean): Remedy[] {
  if (leg.status !== "infeasible") return [];
  const remedies: Remedy[] = [];

  if (leg.compressibleMinutes > 0) {
    remedies.push({
      kind: "compress",
      savedMinutes: leg.compressibleMinutes,
      sufficient: leg.remainingMinutes - leg.compressibleMinutes <= leg.capacityMinutes,
    });
  }

  // Past the deadline there is no pace that works, so don't offer one.
  if (leg.daysLeft > 0) {
    remedies.push({
      kind: "raise-pace",
      hoursPerWeekNeeded: roundUpToHalf(leg.requiredHoursPerDay * DAYS_PER_WEEK),
      sufficient: true,
    });
  }

  if (encore && leg.id === "leg-1") {
    remedies.push({
      kind: "leave-encore",
      extraDays: Math.max(0, road.daysLeft - leg.daysLeft),
      sufficient: road.status !== "infeasible",
    });
  }

  remedies.push({ kind: "trim", minutes: Math.ceil(leg.deficitMinutes), sufficient: true });
  return remedies;
}

export interface CountdownInput {
  /** Epoch ms from the countdown store's single interval. */
  now: number;
  /** The entries in play — the filtered road, not necessarily the whole catalog. */
  entries: readonly Entry[];
  watched: Readonly<Record<EntryId, ISODate>>;
  target: TargetEvent;
  /** The splitting milestone (Endgame Encore); `null` disables Encore mode entirely. */
  milestone: Milestone | null;
  encoreMode: boolean;
  hoursPerWeek: number;
  /** Cost entries at `solver.minMinutes` instead of full runtime. */
  useAlternatives: boolean;
}

export interface CountdownState {
  /** Encore mode is on **and** the catalog has a milestone to split on. */
  encore: boolean;
  /** The splitting milestone, whether or not Encore mode is on; `null` if the catalog has none. */
  milestone: Milestone | null;
  /** One leg with Encore off, two with it on — what the bar renders. */
  legs: LegPace[];
  /**
   * The whole scope as a single countdown to the target. Equal to `legs[0]`
   * when Encore is off; with it on this is the road you'd have if you turned
   * Encore off, which is what makes the comparison — and the "leave Encore"
   * remedy — honest.
   */
  road: LegPace;
  /** The worst status across `legs` — what the bar as a whole reports. */
  worstStatus: PaceStatus;
  /** How many days sooner the Encore lands than the target. The beat. */
  encoreLeadDays: number;
}

const STATUS_SEVERITY: Record<PaceStatus, number> = { ok: 0, tight: 1, infeasible: 2 };

export function computeCountdown(input: CountdownInput): CountdownState {
  const { milestone, target } = input;
  const shared = {
    watched: input.watched,
    now: input.now,
    hoursPerWeek: input.hoursPerWeek,
    useAlternatives: input.useAlternatives,
  };

  const road = computeLeg({
    id: "road",
    eventTitle: target.title,
    deadline: target.releaseDate,
    entries: input.entries,
    ...shared,
  });

  const encoreLeadDays = milestone
    ? Math.max(
        0,
        daysUntil(deadlineInstant(target.releaseDate), input.now) -
          daysUntil(deadlineInstant(milestone.date), input.now),
      )
    : 0;

  const encore = input.encoreMode && milestone !== null;
  if (!encore || !milestone) {
    return {
      encore: false,
      milestone,
      legs: [{ ...road, remedies: remediesFor(road, road, false) }],
      road,
      worstStatus: road.status,
      encoreLeadDays,
    };
  }

  const legs = [
    computeLeg({
      id: "leg-1",
      eventTitle: milestone.title,
      deadline: milestone.date,
      entries: input.entries.filter((entry) => entry.leg === 1),
      ...shared,
    }),
    computeLeg({
      id: "leg-2",
      eventTitle: target.title,
      deadline: target.releaseDate,
      entries: input.entries.filter((entry) => entry.leg === 2),
      ...shared,
    }),
  ].map((leg) => ({ ...leg, remedies: remediesFor(leg, road, true) }));

  const worstStatus = legs.reduce<PaceStatus>(
    (worst, leg) => (STATUS_SEVERITY[leg.status] > STATUS_SEVERITY[worst] ? leg.status : worst),
    "ok",
  );

  return { encore: true, milestone, legs, road, worstStatus, encoreLeadDays };
}
